import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Bell, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  listAlertRules,
  createAlertRule,
  deleteAlertRule,
  updateAlertRule,
  listAlertEvents,
  type AlertRule
} from '@/api/fleet';
import { timeAgo } from '@/lib/format';

const TRIGGERS: Array<{
  value: AlertRule['trigger'];
  label: string;
  unit: string;
}> = [
  { value: 'nodes_offline_pct', label: 'Nodes offline (%)', unit: '%' },
  { value: 'earnings_drop_pct', label: 'Earnings drop (%)', unit: '%' },
  { value: 'audit_failure_rate', label: 'Audit failure rate (%)', unit: '%' }
];

export function AlertsScreen() {
  const [tab, setTab] = useState<'rules' | 'events'>('rules');

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Alerts</h1>
        <p className="mt-1 max-w-2xl text-sm text-nurev-muted">
          Set rules to get emailed when your fleet's health deviates. Rules
          evaluate against rolling windows; fired events appear on the Events
          tab.
        </p>
      </header>

      <div className="flex gap-1.5 border-b border-nurev-border">
        <button
          onClick={() => setTab('rules')}
          className={`border-b-2 px-3 py-2 text-sm font-medium transition ${
            tab === 'rules'
              ? 'border-emerald-400 text-emerald-300'
              : 'border-transparent text-nurev-muted hover:text-nurev-text'
          }`}
        >
          Rules
        </button>
        <button
          onClick={() => setTab('events')}
          className={`border-b-2 px-3 py-2 text-sm font-medium transition ${
            tab === 'events'
              ? 'border-emerald-400 text-emerald-300'
              : 'border-transparent text-nurev-muted hover:text-nurev-text'
          }`}
        >
          Events
        </button>
      </div>

      {tab === 'rules' ? <RulesTab /> : <EventsTab />}
    </div>
  );
}

function RulesTab() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data: rules, isLoading } = useQuery({
    queryKey: ['fleet-alert-rules'],
    queryFn: listAlertRules
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAlertRule(id),
    onSuccess: () => {
      toast.success('Rule deleted');
      qc.invalidateQueries({ queryKey: ['fleet-alert-rules'] });
    }
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateAlertRule(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-alert-rules'] })
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> New rule
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
        </div>
      ) : !rules || rules.length === 0 ? (
        <div className="card text-center">
          <Bell className="mx-auto mb-3 h-8 w-8 text-nurev-muted" />
          <p className="text-sm text-nurev-muted">
            No alert rules. Create one to get notified when something needs
            your attention.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rules.map(r => {
            const meta = TRIGGERS.find(t => t.value === r.trigger);
            return (
              <div key={r._id} className="card">
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-nurev-text">{r.name}</h3>
                    <p className="text-xs text-nurev-muted">
                      {meta?.label} &gt; {r.threshold}
                      {meta?.unit} for {r.windowMinutes}m
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={r.enabled}
                      onChange={() =>
                        toggle.mutate({ id: r._id, enabled: !r.enabled })
                      }
                    />
                    <span className="text-nurev-muted">
                      {r.enabled ? 'On' : 'Off'}
                    </span>
                  </label>
                </div>
                <div className="flex items-center justify-between text-xs text-nurev-muted">
                  <span>Notify {r.notifyEmail || 'account email'}</span>
                  <button
                    onClick={() =>
                      window.confirm(`Delete rule "${r.name}"?`) &&
                      remove.mutate(r._id)
                    }
                    className="inline-flex items-center gap-1 text-red-300 hover:underline"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <CreateRuleModal
          onClose={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['fleet-alert-rules'] });
          }}
        />
      )}
    </div>
  );
}

function EventsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-alert-events'],
    queryFn: () => listAlertEvents({ limit: 100 })
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 text-nurev-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card text-center text-sm text-nurev-muted">
        No fired events yet. When a rule fires it'll show up here with the
        context that triggered it.
      </div>
    );
  }

  return (
    <div className="card overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-nurev-border bg-white/[0.02] text-left text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
            <th className="px-4 py-3">Rule</th>
            <th className="px-4 py-3">Fired</th>
            <th className="px-4 py-3">Acknowledged</th>
            <th className="px-4 py-3">Context</th>
          </tr>
        </thead>
        <tbody>
          {data.map(e => (
            <tr key={e._id} className="border-b border-nurev-border/60">
              <td className="px-4 py-3 font-medium text-nurev-text">
                {e.ruleName}
              </td>
              <td className="px-4 py-3 text-nurev-muted">{timeAgo(e.firedAt)}</td>
              <td className="px-4 py-3 text-nurev-muted">
                {e.acknowledgedAt ? (
                  <span className="text-emerald-300">
                    {timeAgo(e.acknowledgedAt)}
                  </span>
                ) : (
                  <span className="text-amber-300">Open</span>
                )}
              </td>
              <td className="px-4 py-3">
                <code className="text-[11px] text-nurev-muted">
                  {JSON.stringify(e.context).slice(0, 80)}
                </code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateRuleModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<AlertRule['trigger']>(
    'nodes_offline_pct'
  );
  const [threshold, setThreshold] = useState(5);
  const [windowMinutes, setWindowMinutes] = useState(10);
  const [notifyEmail, setNotifyEmail] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      createAlertRule({
        name,
        trigger,
        threshold,
        windowMinutes,
        enabled: true,
        notifyEmail: notifyEmail || undefined
      }),
    onSuccess
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name) return;
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-2xl border border-emerald-500/30 bg-nurev-surface p-6"
      >
        <h2 className="text-lg font-bold text-nurev-text">New alert rule</h2>
        <div>
          <label className="label">Rule name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="More than 5% offline for 10min"
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">Trigger</label>
          <select
            value={trigger}
            onChange={e => setTrigger(e.target.value as AlertRule['trigger'])}
            className="input"
          >
            {TRIGGERS.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Threshold (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={threshold}
              onChange={e => setThreshold(parseFloat(e.target.value) || 0)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Window (minutes)</label>
            <input
              type="number"
              min={1}
              value={windowMinutes}
              onChange={e => setWindowMinutes(parseInt(e.target.value, 10) || 1)}
              className="input"
            />
          </div>
        </div>
        <div>
          <label className="label">Notify email (optional)</label>
          <input
            type="email"
            value={notifyEmail}
            onChange={e => setNotifyEmail(e.target.value)}
            placeholder="defaults to account email"
            className="input"
          />
        </div>
        {mut.error && (
          <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {(mut.error as Error).message}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={mut.isPending} className="btn-primary">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create rule'}
          </button>
        </div>
      </form>
    </div>
  );
}
